/* ============================================================================
 * Section: Search Modal — PURRZ
 * - Products-only predictive fetch, abortable, debounced
 * - Escape to close, focus trap, dialog semantics
 * - Recent searches (per-browser), keyboard navigation
 * Cards come from snippets/c-search-product-card.liquid (search-only).
 * ========================================================================== */

const RECENT_KEY = 'purrz:recent-searches';
const RECENT_MAX = 6;

class SSearchModal extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.productLimit = parseInt(this.dataset.productLimit, 10) || 8;
    this.showRecent = this.dataset.showRecent !== 'false';
    this.body = document.body;

    this.overlay = document.querySelector('.js-overlay');
    this.scrim = this.querySelector('[data-scrim]');
    this.input = this.querySelector('.js-search-modal__search-input');
    this.form = this.querySelector('.js-search-modal__form');
    this.resultsWrap = this.querySelector('.js-search-modal__body');
    this.closeBtn = this.querySelector('.js-search-modal__close-btn');
    this.emptyTemplate = this.querySelector('.t-search-modal__empty');

    this._abort = null;
    this._lastFocused = null;
    this._activeIndex = -1;

    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
    this.handleOpenFromPubSub = this.handleOpenFromPubSub.bind(this);
  }

  connectedCallback() {
    this.closeBtn?.addEventListener('click', this.close);
    this.scrim?.addEventListener('click', this.close);
    this.form?.addEventListener('submit', this.onSubmit);
    this.resultsWrap?.addEventListener('click', this.onBodyClick);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('keydown', this.onKeydown);
    this.onResize();

    this.modalCloseUnsubscriber = window.PubSub?.subscribe?.('search-modal:close', this.close);
    this.modalOpenUnsubscriber = window.PubSub?.subscribe?.('search-modal:open', this.handleOpenFromPubSub);

    if (this.input) this.input.addEventListener('input', this.debounce(this.onInput, 260));
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener('click', this.close);
    this.scrim?.removeEventListener('click', this.close);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.resultsWrap?.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeydown);
    this.input?.removeEventListener('input', this.onInput);
    this._abort?.abort?.();

    try {
      if (typeof this.modalCloseUnsubscriber === 'function') this.modalCloseUnsubscriber();
      if (typeof this.modalOpenUnsubscriber === 'function') this.modalOpenUnsubscriber();
    } catch (e) { /* PubSub may not return unsubscribers */ }
  }

  debounce(fn, ms = 260) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  /* ----------------------------------------------------------- positioning */

  onResize() {
    const bar = document.querySelector('.section-announcement-bar');
    const header = document.querySelector('.site-header, header.s-header, #SiteHeader');
    const offset = (bar ? bar.offsetHeight : 0) + (header ? header.offsetHeight : 0);
    this.style.setProperty('--sm-top', offset + 'px');
  }

  /* ---------------------------------------------------------- open / close */

  handleOpenFromPubSub(a, b) {
    const payload = b !== undefined ? b : a;
    let q = '';
    if (typeof payload === 'string') q = payload;
    else if (payload && typeof payload === 'object') {
      q = payload.term || payload.q || payload.searchTerm || payload.value || '';
    } else {
      q = this.input?.value || '';
    }
    this.open(q);
  }

  open(initialValue) {
    this._lastFocused = document.activeElement;

    this.body.classList.add('modal-open');
    this.overlay?.classList.add('is-open');
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.overlay?.addEventListener('click', this.close);
    this.onResize();

    const q = typeof initialValue === 'string' ? initialValue : (this.input?.value || '');
    if (this.input && typeof initialValue === 'string' && initialValue) this.input.value = initialValue;

    this.classList.toggle('is-empty', !this.normaliseQuery(q));
    requestAnimationFrame(() => this.fetchResults(q));
    setTimeout(() => {
      this.input?.focus();
      this.input?.select();
    }, 150);
  }

  close() {
    this.body.classList.remove('modal-open');
    this.overlay?.classList.remove('is-open');
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay?.removeEventListener('click', this.close);
    this._abort?.abort?.();
    this._activeIndex = -1;

    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
      this._lastFocused = null;
    }
  }

  get isOpen() { return this.classList.contains('is-open'); }

  /* ------------------------------------------------------------- keyboard */

  onKeydown(evt) {
    if (!this.isOpen) return;

    if (evt.key === 'Escape') {
      evt.preventDefault();
      this.close();
      return;
    }

    if (evt.key === 'Tab') {
      this.trapFocus(evt);
      return;
    }

    if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
      const items = this.navigableItems();
      if (!items.length) return;
      evt.preventDefault();
      const dir = evt.key === 'ArrowDown' ? 1 : -1;
      this._activeIndex = (this._activeIndex + dir + items.length) % items.length;
      this.highlight(items);
      return;
    }

    if (evt.key === 'Enter') {
      const items = this.navigableItems();
      const active = items[this._activeIndex];
      if (active) {
        evt.preventDefault();
        this.rememberTerm(this.input?.value || '');
        active.click();
      }
    }
  }

  navigableItems() {
    return Array.from(this.querySelectorAll(
      '.s-search-card, .s-search-modal__item a, .s-search-modal__item button'
    )).filter((el) => el.offsetParent !== null);
  }

  highlight(items) {
    items.forEach((el, i) => {
      const host = el.closest('.s-search-modal__item-product, .s-search-modal__item') || el;
      host.classList.toggle('is-active', i === this._activeIndex);
      el.classList.toggle('is-active', i === this._activeIndex);
      if (i === this._activeIndex) el.scrollIntoView({ block: 'nearest' });
    });
  }

  trapFocus(evt) {
    const focusable = Array.from(this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  /* --------------------------------------------------------------- recents */

  readRecent() {
    if (!this.showRecent) return [];
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  rememberTerm(term) {
    const t = (term || '').trim();
    if (!this.showRecent || t.length < 2) return;
    try {
      const list = this.readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase());
      list.unshift(t);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* private mode or blocked storage */ }
  }

  clearRecent() {
    try { window.localStorage.removeItem(RECENT_KEY); } catch (e) { /* ignore */ }
    this.renderRecent();
  }

  escapeHtml(str) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(str).replace(/[&<>"']/g, (c) => map[c]);
  }

  renderRecent() {
    const col = this.querySelector('[data-recent]');
    if (!col) return;

    const ul = col.querySelector('[data-recent-list]');
    if (!ul) return;

    const list = this.readRecent();

    if (!list.length) {
      col.hidden = true;
      ul.innerHTML = '';
      return;
    }

    col.hidden = false;
    ul.innerHTML = list.map((term) => {
      const safe = this.escapeHtml(term);
      return '<li class="s-search-modal__item"><button type="button" data-recent-term="' + safe + '">' + safe + '</button></li>';
    }).join('');
  }

  onBodyClick(evt) {
    const clear = evt.target.closest('[data-recent-clear]');
    if (clear) {
      evt.preventDefault();
      this.clearRecent();
      return;
    }

    const recent = evt.target.closest('[data-recent-term]');
    if (recent && this.input) {
      evt.preventDefault();
      this.input.value = recent.dataset.recentTerm;
      this.input.focus();
      this.fetchResults(this.input.value);
      return;
    }

    const link = evt.target.closest('.s-search-card, .s-search-modal__item a');
    if (link) this.rememberTerm(this.input?.value || '');
  }

  /* --------------------------------------------------------------- results */

  onSubmit(evt) {
    const term = this.normaliseQuery(this.input?.value || '');
    if (!term) {
      // an empty query would navigate to /search?q= and show everything
      evt?.preventDefault();
      this.input?.focus();
      return;
    }
    this.rememberTerm(term);
  }

  onInput() {
    const term = (this.input?.value || '').trim();
    this.classList.toggle('is-empty', !term);
    this.fetchResults(term);
  }

  /* The old isUnsafeQuery rejected & ' + ( ) and more, so "cat & dog" and
     "dog's bed" silently fell through to the empty state. Shopify escapes the
     query itself; we only guard length and strip control characters. */
  normaliseQuery(q) {
    return String(q || '')
      .split('')
      .filter((ch) => ch.charCodeAt(0) > 31 && ch.charCodeAt(0) !== 127)
      .join('')
      .trim()
      .slice(0, 120);
  }

  showEmptyState() {
    this.resultsWrap.innerHTML = this.emptyTemplate?.innerHTML || '';
    this.renderRecent();
  }

  async fetchResults(q) {
    const term = this.normaliseQuery(q);
    this._activeIndex = -1;

    if (!term) {
      this.showEmptyState();
      return;
    }

    this._abort?.abort?.();
    this._abort = new AbortController();
    this.classList.add('is-loading');

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection',
      'resources[limit]': String(this.productLimit),
      'resources[options][unavailable_products]': 'last',
      section_id: this.sectionId
    });

    try {
      const res = await fetch(
        window.Shopify.routes.root + 'search/suggest?' + params.toString(),
        { signal: this._abort.signal }
      );
      const html = await res.text();
      if (!res.ok) throw new Error(String(res.status));

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const body = doc.querySelector('.js-search-modal__body');
      this.resultsWrap.innerHTML = body ? body.innerHTML : '';

      this.renderRecent();
      window.PubSub?.publish?.('product-card-image:load');
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error(err);
      this.showEmptyState();
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('s-search-modal')) {
  customElements.define('s-search-modal', SSearchModal);
}
